import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../firebase";

const PAGE_SIZE_OPTIONS = [6, 12, 24, 1000];

function normalizeText(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export default function AdminVendorProductsValidation() {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);

  const [currentFilter, setCurrentFilter] = useState("pending");
  const [vendorFilter, setVendorFilter] = useState("");

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [adminNotes, setAdminNotes] = useState({});
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingId, setUpdatingId] = useState("");

  const [pageCursors, setPageCursors] = useState({});
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const loadVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where("role", "==", "vendor"))
      );

      const items = snap.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            uid: data.uid || d.id,
            label:
              data.shopName ||
              data.displayName ||
              data.email ||
              `Vendeur ${d.id.slice(0, 6)}`,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));

      setVendors(items);
    } catch (err) {
      console.error("Erreur chargement vendeurs:", err);
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const getBaseQuery = useCallback(() => {
    const constraints = [
      where("validationStatus", "==", currentFilter),
    ];

    if (vendorFilter) {
      constraints.push(where("vendorId", "==", vendorFilter));
    }

    constraints.push(orderBy("__name__", "desc"));

    return query(collection(db, "productsShop"), ...constraints);
  }, [currentFilter, vendorFilter]);

  const loadTotalCount = useCallback(async () => {
    try {
      const constraints = [
        where("validationStatus", "==", currentFilter),
      ];

      if (vendorFilter) {
        constraints.push(where("vendorId", "==", vendorFilter));
      }

      const countSnap = await getCountFromServer(
        query(collection(db, "productsShop"), ...constraints)
      );

      setTotalItems(countSnap.data().count || 0);
    } catch (err) {
      console.error("Erreur getCountFromServer:", err);
      setTotalItems(0);
    }
  }, [currentFilter, vendorFilter]);

  const loadPage = useCallback(
    async (pageNumber = 1, options = {}) => {
      const { resetCursors = false } = options;

      setLoading(true);
      setError("");
      setSuccessMsg("");

      try {
        let qRef = query(getBaseQuery(), limit(pageSize + 1));

        if (pageNumber > 1 && pageCursors[pageNumber - 1]) {
          qRef = query(
            getBaseQuery(),
            startAfter(pageCursors[pageNumber - 1]),
            limit(pageSize + 1)
          );
        }

        const snap = await getDocs(qRef);
        const docs = snap.docs;

        const pageDocs = docs.slice(0, pageSize);
        const hasMore = docs.length > pageSize;

        const items = pageDocs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setProducts(items);
        setHasNextPage(hasMore);

        if (pageDocs.length > 0) {
          const lastVisible = pageDocs[pageDocs.length - 1];
          setPageCursors((prev) => {
            const nextState = resetCursors ? {} : { ...prev };
            nextState[pageNumber] = lastVisible;
            return nextState;
          });
        } else if (resetCursors) {
          setPageCursors({});
        }

        setCurrentPage(pageNumber);
      } catch (err) {
        console.error("Erreur chargement produits admin:", err);
        setError("Erreur lors du chargement des produits.");
        setProducts([]);
        setHasNextPage(false);
      } finally {
        setLoading(false);
      }
    },
    [getBaseQuery, pageSize, pageCursors]
  );

  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
    loadPage(1, { resetCursors: true });
    loadTotalCount();
  }, [currentFilter, vendorFilter, pageSize]);

  const handleNoteChange = (productId, value) => {
    setAdminNotes((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleUpdateStatus = async (product, newStatus) => {
    setError("");
    setSuccessMsg("");
    setUpdatingId(product.id);

    try {
      const productRef = doc(db, "productsShop", product.id);
      const note = adminNotes[product.id]?.trim() ?? product.adminNote ?? null;

      const dataUpdate = {
        validationStatus: newStatus,
        updatedAt: serverTimestamp(),
        adminNote: note || null,
        isValidated: newStatus === "validated",
        isVisible: newStatus === "validated",
      };

      await updateDoc(productRef, dataUpdate);

      setSuccessMsg(
        newStatus === "validated"
          ? "Produit validé et rendu visible sur le site."
          : "Produit refusé et masqué du site."
      );

      setAdminNotes((prev) => {
        const copy = { ...prev };
        delete copy[product.id];
        return copy;
      });

      await loadTotalCount();
      await loadPage(currentPage);
    } catch (err) {
      console.error("Erreur mise à jour statut produit:", err);
      setError("Impossible de mettre à jour le statut du produit.");
    } finally {
      setUpdatingId("");
    }
  };

  const renderStatusLabel = (product) => {
    const s =
      product.validationStatus ||
      (product.isValidated ? "validated" : "pending");

    if (s === "validated") return "Validé / Publié";
    if (s === "rejected") return "Refusé";
    return "En attente de validation";
  };

  const renderStatusBadgeClass = (product) => {
    const s =
      product.validationStatus ||
      (product.isValidated ? "validated" : "pending");

    if (s === "validated") return "avp-status avp-status-valid";
    if (s === "rejected") return "avp-status avp-status-refused";
    return "avp-status avp-status-pending";
  };

  const getProductName = (p) =>
    p?.shortName ||
    p?.name ||
    p?.Name ||
    p?.title ||
    p?.Title ||
    p?.productName ||
    p?.ProductName ||
    "Produit sans nom";

  const getProductPrice = (p) =>
    p?.salePrice ??
    p?.SalePrice ??
    p?.price ??
    p?.Price ??
    p?.Cass_Stock_Price ??
    null;

  const selectedVendorLabel = useMemo(() => {
    if (!vendorFilter) return "Tous les vendeurs";
    const found = vendors.find((v) => v.uid === vendorFilter);
    return found?.label || "Vendeur sélectionné";
  }, [vendorFilter, vendors]);

  const filteredProducts = useMemo(() => {
    const needle = normalizeText(search);
    if (!needle) return products;

    return products.filter((p) => {
      const haystack = normalizeText(
        [
          getProductName(p),
          p?.vendorName,
          p?.categoryLabel,
          p?.subCategoryLabel,
          p?.barcode,
          p?.description,
          p?.adminNote,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(needle);
    });
  }, [products, search]);

  const currentItemsCount = filteredProducts.length;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex =
    totalItems === 0 ? 0 : Math.min(currentPage * pageSize, totalItems);
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handlePrevPage = async () => {
    if (currentPage <= 1 || loading) return;
    await loadPage(currentPage - 1);
    scrollToTop();
  };

  const handleNextPage = async () => {
    if (!hasNextPage || loading) return;
    await loadPage(currentPage + 1);
    scrollToTop();
  };

  return (
    <div className="avp-shell">
      <main className="avp-main">
        <header className="avp-header">
          <div>
            <h1 className="avp-page-title">Validation des produits vendeurs</h1>
            <p className="avp-page-subtitle">
              Consultez les produits ajoutés par les vendeurs, validez ou refusez
              avec une note, et contrôlez leur visibilité sur la boutique GoShop.
            </p>
          </div>

          <div className="avp-header-chip">
            <span className="avp-header-chip-label">Éléments / page</span>
            <select
              className="avp-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </header>

        <div className="avp-toolbar">
          <div className="avp-tabs">
            <button
              type="button"
              className={`avp-tab-btn ${
                currentFilter === "pending" ? "avp-tab-btn-active" : ""
              }`}
              onClick={() => setCurrentFilter("pending")}
            >
              En attente
            </button>

            <button
              type="button"
              className={`avp-tab-btn ${
                currentFilter === "validated" ? "avp-tab-btn-active" : ""
              }`}
              onClick={() => setCurrentFilter("validated")}
            >
              Validés
            </button>

            <button
              type="button"
              className={`avp-tab-btn ${
                currentFilter === "rejected" ? "avp-tab-btn-active" : ""
              }`}
              onClick={() => setCurrentFilter("rejected")}
            >
              Refusés
            </button>
          </div>

          <div className="avp-controls">
            <select
              className="avp-select"
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              disabled={loadingVendors}
            >
              <option value="">Tous les vendeurs</option>
              {vendors.map((vendor) => (
                <option key={vendor.uid} value={vendor.uid}>
                  {vendor.label}
                </option>
              ))}
            </select>

            <input
              type="search"
              className="avp-search"
              placeholder="Rechercher un produit, vendeur, catégorie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="avp-summary-row">
          <div className="avp-summary-card">
            <span className="avp-summary-label">Filtre actuel</span>
            <strong>
              {currentFilter === "pending"
                ? "Produits en attente"
                : currentFilter === "validated"
                ? "Produits validés"
                : "Produits refusés"}
            </strong>
          </div>

          <div className="avp-summary-card">
            <span className="avp-summary-label">Vendeur</span>
            <strong>{selectedVendorLabel}</strong>
          </div>

          <div className="avp-summary-card">
            <span className="avp-summary-label">Total en base</span>
            <strong>{loading ? "..." : totalItems}</strong>
          </div>

          <div className="avp-summary-card">
            <span className="avp-summary-label">Page actuelle</span>
            <strong>
              {currentPage} / {totalPages}
            </strong>
          </div>

          <div className="avp-summary-card">
            <span className="avp-summary-label">Résultats affichés</span>
            <strong>{currentItemsCount}</strong>
          </div>
        </div>

        {error && <div className="avp-alert avp-alert-error">{error}</div>}
        {successMsg && (
          <div className="avp-alert avp-alert-success">{successMsg}</div>
        )}

        <section>
          {loading ? (
            <div className="avp-loading">Chargement des produits...</div>
          ) : currentItemsCount === 0 ? (
            <div className="avp-empty">
              {search
                ? "Aucun produit trouvé pour cette recherche sur la page actuelle."
                : "Aucun produit trouvé pour ce filtre."}
            </div>
          ) : (
            <>
              <div className="avp-grid">
                {filteredProducts.map((p) => {
                  const price = getProductPrice(p);
                  const currentNote = adminNotes[p.id] ?? p.adminNote ?? "";

                  return (
                    <article key={p.id} className="avp-card">
                      <div className="avp-image">
                        {p.ImageUrl ? (
                          <img
                            src={p.ImageUrl}
                            alt={getProductName(p)}
                            loading="lazy"
                          />
                        ) : (
                          <div className="avp-image-placeholder">
                            <span>{getProductName(p)?.charAt(0) || "P"}</span>
                          </div>
                        )}
                      </div>

                      <div className="avp-body">
                        <div className="avp-body-header">
                          <div>
                            <h3 className="avp-title">{getProductName(p)}</h3>

                            {p.vendorName && (
                              <p className="avp-vendor">
                                Vendeur : <strong>{p.vendorName}</strong>
                              </p>
                            )}
                          </div>

                          <span className={renderStatusBadgeClass(p)}>
                            {renderStatusLabel(p)}
                          </span>
                        </div>

                        <p className="avp-price">
                          {typeof price === "number"
                            ? `${price.toLocaleString()} FCFA`
                            : "Prix non défini"}
                        </p>

                        {(p.categoryLabel || p.subCategoryLabel) && (
                          <p className="avp-meta">
                            Catégorie : {p.categoryLabel || "—"}
                            {p.subCategoryLabel
                              ? ` · ${p.subCategoryLabel}`
                              : ""}
                          </p>
                        )}

                        {typeof p.stock === "number" && (
                          <p className="avp-meta">Stock : {p.stock}</p>
                        )}

                        {p.barcode && (
                          <p className="avp-meta">Code-barres : {p.barcode}</p>
                        )}

                        {p.description && (
                          <p className="avp-description">
                            {p.description.length > 150
                              ? p.description.slice(0, 150) + "…"
                              : p.description}
                          </p>
                        )}

                        <div className="avp-note-block">
                          <label className="avp-note-label">
                            Note admin (motif, remarques)
                          </label>
                          <textarea
                            rows={2}
                            value={currentNote}
                            onChange={(e) =>
                              handleNoteChange(p.id, e.target.value)
                            }
                            placeholder="Ex : Photos floues, manque d'information, OK pour la mise en ligne..."
                          />
                        </div>

                        <div className="avp-actions">
                          {currentFilter !== "rejected" && (
                            <button
                              type="button"
                              className="avp-btn avp-btn-secondary"
                              onClick={() =>
                                handleUpdateStatus(p, "rejected")
                              }
                              disabled={updatingId === p.id}
                            >
                              {updatingId === p.id
                                ? "Traitement..."
                                : "Refuser"}
                            </button>
                          )}

                          {currentFilter !== "validated" && (
                            <button
                              type="button"
                              className="avp-btn avp-btn-primary"
                              onClick={() =>
                                handleUpdateStatus(p, "validated")
                              }
                              disabled={updatingId === p.id}
                            >
                              {updatingId === p.id
                                ? "Traitement..."
                                : "Valider et publier"}
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="avp-pagination-wrap">
                <div className="avp-pagination-info">
                  Affichage de <strong>{startIndex}</strong> à{" "}
                  <strong>{endIndex}</strong> sur <strong>{totalItems}</strong>{" "}
                  produit(s)
                </div>

                <div className="avp-pagination">
                  <button
                    type="button"
                    className="avp-page-btn"
                    disabled={currentPage <= 1 || loading}
                    onClick={handlePrevPage}
                  >
                    ← Précédent
                  </button>

                  <div className="avp-page-current">
                    Page {currentPage} / {totalPages}
                  </div>

                  <button
                    type="button"
                    className="avp-page-btn"
                    disabled={!hasNextPage || loading}
                    onClick={handleNextPage}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        <style>{`
          .avp-shell {
            display: flex;
            justify-content: center;
            padding: 24px 16px 40px;
            background: #f8fafc;
            min-height: 100vh;
          }

          .avp-main {
            width: 100%;
            max-width: 1180px;
            font-size: 14px;
            line-height: 1.5;
          }

          .avp-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 16px;
          }

          .avp-page-title {
            font-size: 26px;
            font-weight: 800;
            color: #0f172a;
            margin: 0 0 6px;
          }

          .avp-page-subtitle {
            font-size: 13px;
            color: #4b5563;
            margin: 0;
            max-width: 820px;
          }

          .avp-header-chip {
            display: flex;
            flex-direction: column;
            gap: 6px;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 14px;
            padding: 10px 12px;
            box-shadow: 0 8px 18px rgba(15,23,42,0.04);
            min-width: 150px;
          }

          .avp-header-chip-label {
            font-size: 11px;
            color: #6b7280;
          }

          .avp-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 14px;
          }

          .avp-tabs {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px;
            border-radius: 999px;
            background: #e5e7eb;
          }

          .avp-tab-btn {
            border: none;
            background: transparent;
            padding: 7px 14px;
            border-radius: 999px;
            font-size: 13px;
            font-weight: 600;
            color: #4b5563;
            cursor: pointer;
          }

          .avp-tab-btn-active {
            background: #ffffff;
            color: #111827;
            box-shadow: 0 1px 4px rgba(15,23,42,0.15);
          }

          .avp-controls {
            display: flex;
            gap: 8px;
            align-items: center;
            flex-wrap: wrap;
          }

          .avp-search,
          .avp-select {
            border-radius: 12px;
            border: 1px solid #d1d5db;
            background: #fff;
            font-size: 13px;
            padding: 9px 12px;
            outline: none;
          }

          .avp-search {
            min-width: 320px;
          }

          .avp-search:focus,
          .avp-select:focus {
            border-color: #4f46e5;
            box-shadow: 0 0 0 1px rgba(79,70,229,0.18);
          }

          .avp-summary-row {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-bottom: 18px;
          }

          .avp-summary-card {
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 14px;
            padding: 10px 12px;
            min-width: 170px;
            box-shadow: 0 8px 18px rgba(15,23,42,0.04);
          }

          .avp-summary-label {
            display: block;
            font-size: 11px;
            color: #6b7280;
            margin-bottom: 4px;
          }

          .avp-summary-card strong {
            font-size: 14px;
            color: #111827;
          }

          .avp-alert {
            margin-bottom: 10px;
            padding: 8px 10px;
            border-radius: 10px;
            font-size: 13px;
          }

          .avp-alert-error {
            background: #fee2e2;
            color: #b91c1c;
          }

          .avp-alert-success {
            background: #dcfce7;
            color: #166534;
          }

          .avp-loading,
          .avp-empty {
            font-size: 13px;
            color: #6b7280;
            padding: 14px 0;
          }

          .avp-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
          }

          @media (max-width: 900px) {
            .avp-grid {
              grid-template-columns: minmax(0, 1fr);
            }
          }

          .avp-card {
            background: #ffffff;
            border-radius: 18px;
            border: 1px solid #e5e7eb;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 12px 30px rgba(15,23,42,0.06);
          }

          .avp-image {
            width: 100%;
            aspect-ratio: 4 / 3;
            background: #f9fafb;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .avp-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .avp-image-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: 700;
            color: #9ca3af;
          }

          .avp-body {
            padding: 12px;
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .avp-body-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 8px;
          }

          .avp-title {
            font-size: 15px;
            font-weight: 700;
            color: #111827;
            margin: 0 0 2px;
          }

          .avp-vendor {
            font-size: 12px;
            color: #6b7280;
            margin: 0;
          }

          .avp-price {
            font-size: 14px;
            font-weight: 700;
            color: #16a34a;
            margin: 0;
          }

          .avp-meta {
            font-size: 12px;
            color: #6b7280;
            margin: 0;
          }

          .avp-description {
            font-size: 12px;
            color: #4b5563;
            margin: 4px 0 0;
          }

          .avp-note-block {
            margin-top: 6px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .avp-note-label {
            font-size: 12px;
            color: #111827;
            font-weight: 600;
          }

          .avp-note-block textarea {
            border-radius: 10px;
            border: 1px solid #d1d5db;
            padding: 8px 10px;
            font-size: 12px;
            resize: vertical;
            min-height: 46px;
          }

          .avp-note-block textarea:focus {
            border-color: #4f46e5;
            outline: none;
            box-shadow: 0 0 0 1px rgba(79,70,229,0.20);
          }

          .avp-actions {
            margin-top: 8px;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            flex-wrap: wrap;
          }

          .avp-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 8px 14px;
            border-radius: 999px;
            border: 1px solid transparent;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
          }

          .avp-btn-primary {
            background: #1e17eb;
            color: #ffffff;
            border-color: #1e17eb;
          }

          .avp-btn-primary:hover:not(:disabled) {
            background: #111827;
          }

          .avp-btn-secondary {
            background: #ffffff;
            color: #b91c1c;
            border-color: #fecaca;
          }

          .avp-btn-secondary:hover:not(:disabled) {
            background: #fee2e2;
          }

          .avp-btn:disabled {
            opacity: 0.7;
            cursor: wait;
          }

          .avp-status {
            display: inline-flex;
            align-items: center;
            padding: 4px 8px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            white-space: nowrap;
          }

          .avp-status-valid {
            background: #dcfce7;
            color: #166534;
          }

          .avp-status-pending {
            background: #fef9c3;
            color: #92400e;
          }

          .avp-status-refused {
            background: #fee2e2;
            color: #b91c1c;
          }

          .avp-pagination-wrap {
            margin-top: 18px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            padding: 12px 0 4px;
          }

          .avp-pagination-info {
            font-size: 12px;
            color: #6b7280;
          }

          .avp-pagination {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .avp-page-btn {
            border: 1px solid #d1d5db;
            background: #fff;
            color: #111827;
            border-radius: 999px;
            padding: 8px 14px;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
          }

          .avp-page-btn:hover:not(:disabled) {
            background: #f9fafb;
          }

          .avp-page-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .avp-page-current {
            font-size: 12px;
            color: #374151;
            font-weight: 700;
            padding: 0 4px;
          }

          @media (max-width: 640px) {
            .avp-search {
              min-width: 100%;
              width: 100%;
            }

            .avp-controls {
              width: 100%;
            }

            .avp-select {
              width: 100%;
            }

            .avp-pagination-wrap {
              flex-direction: column;
              align-items: flex-start;
            }
          }
        `}</style>
      </main>
    </div>
  );
}